import { mappedColors, typography } from '@pixee/design-system'
import { keepPreviousData } from '@tanstack/react-query'
import { useCallback } from 'react'
import { match, P } from 'ts-pattern'
import {
  useFindingQuickViewState,
  useGetScanAndRepository,
  useGetScanCancellation,
  useGetScanLifecycleState,
  type ScanLifecycleState,
} from '../hooks/scan-hooks.ts'
import { useShowMutationError } from '../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../services/toast-service.tsx'
import { useGetScanFindings, usePostAnalyzeScan } from '../utils/api-client/api-hooks.ts'
import type {
  FindingAggregateResponseStatistics,
  Repository,
  ScanResponse,
  Tool,
} from '../utils/api-client/api-schemas.ts'
import { isIsoTimestampInPast } from '../utils/date-utils.ts'
import { isScaTool } from '../utils/detector-utils.ts'
import { getFixableFindingsCount, getScaProcessedTotal } from '../utils/finding-statistics.ts'
import { initializeFirstBatch } from '../utils/first-batch-tracking.ts'
import { useIsStalePlaceholderData } from '../utils/query-utils.ts'
import { AnalysisMetrics } from './analysis-metrics.tsx'
import { CompositionBarWithLabel } from './composition-bar/composition-bar-with-label'
import {
  getFixSelectedSegments,
  getScaSelectedSegments,
  getTriageSelectedSegments,
  handleFixSegmentClick,
  handleScaSegmentClick,
  handleTriageSegmentClick,
  type FixSegment,
  type ScaSegment,
  type TriageSegment,
} from './composition-bar/composition-utils'
import {
  QuickViewCompositionBars,
  type QuickViewCompositionBarsProps,
} from './composition-bar/quick-view-composition-bars'
import * as styles from './scan-details-header.css.ts'
import { Spinner } from './spinner.tsx'
import { mapFilterStateToGetScanFindingsParams, useFilterStateStore } from './tables/filter-state-store.tsx'
import { getFindingQuickViewConfig, type FindingQuickViewId } from './tables/finding-quick-view-config'

// #region Hook

type UseScanDetailsHeaderBaseOptions = {
  scanId: string
}

export function useScanDetailsHeaderBase({ scanId }: UseScanDetailsHeaderBaseOptions) {
  const { scan, isScanLoading, isScanError, repository, isRepositoryLoading, isRepositoryError } =
    useGetScanAndRepository({ scanId })
  const { handleAnalyzeScan } = useAnalyzeScan({ scanId })
  const { scanLifecycleState, isScanLifecycleLoading, isScanLifecycleError, totalFindings, skipFirstBatch } =
    useGetScanLifecycleState({ scanId })
  const { selectedQuickView } = useFindingQuickViewState({ scanId })
  const quickViewConfig = getFindingQuickViewConfig(selectedQuickView)

  const filterState = useFilterStateStore(state => state)
  const isFiltersActive = !filterState.isInitialState()

  const filteredParams = mapFilterStateToGetScanFindingsParams(quickViewConfig.defaultFilterState)
  const { data: filteredData, isPlaceholderData } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    ...filteredParams,
    placeholderData: keepPreviousData,
  })
  const isStalePlaceholderData = useIsStalePlaceholderData(isPlaceholderData)
  const isFilteredDataTransitioning = isStalePlaceholderData

  const filteredStatistics: FindingAggregateResponseStatistics | undefined = filteredData
    ? {
        total: filteredData.total,
        completed_analysis: filteredData.completed_analysis,
        in_progress_analysis: filteredData.in_progress_analysis,
        triage: filteredData.triage,
        fix: filteredData.fix,
        sca: filteredData.sca,
        composite: filteredData.composite,
      }
    : undefined

  // Second query: stats reflecting the user's active table filters (for label numerator/denominator)
  const activeFilterParams = mapFilterStateToGetScanFindingsParams(filterState)
  const { data: activeFilterData } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    ...activeFilterParams,
    placeholderData: keepPreviousData,
  })

  const activeFilterStatistics: FindingAggregateResponseStatistics | undefined =
    isFiltersActive && activeFilterData
      ? {
          total: activeFilterData.total,
          completed_analysis: activeFilterData.completed_analysis,
          in_progress_analysis: activeFilterData.in_progress_analysis,
          triage: activeFilterData.triage,
          fix: activeFilterData.fix,
          sca: activeFilterData.sca,
          composite: activeFilterData.composite,
        }
      : undefined

  const handleAnalyzeScanWithBatchTracking = useCallback(() => {
    handleAnalyzeScan(totalFindings)
  }, [handleAnalyzeScan, totalFindings])

  const isScanInputsExpired = isIsoTimestampInPast(scan?.expires_at)

  const onTriageSegmentClick = useCallback(
    (label: TriageSegment) => handleTriageSegmentClick(label, filterState),
    [filterState]
  )
  const onFixSegmentClick = useCallback((label: FixSegment) => handleFixSegmentClick(label, filterState), [filterState])
  const onScaSegmentClick = useCallback((label: ScaSegment) => handleScaSegmentClick(label, filterState), [filterState])

  const triageSelectedSegments = getTriageSelectedSegments(filterState)
  const fixSelectedSegments = getFixSelectedSegments(filterState)
  const scaSelectedSegments = getScaSelectedSegments(filterState)

  const { hasCancellation, latestCancellationTimestamp } = useGetScanCancellation({ scanId })

  return {
    scan,
    isScanLoading,
    isScanError,
    repository,
    isRepositoryLoading,
    isRepositoryError,
    scanDetailsHeaderState: scanLifecycleState,
    isScanDetailsHeaderStateLoading: isScanLifecycleLoading,
    isScanDetailsHeaderStateError: isScanLifecycleError,
    onAnalyzeScan: handleAnalyzeScanWithBatchTracking,
    onSkipToInProgressAnalysis: skipFirstBatch,
    isScanInputsExpired,
    hasCancellation,
    latestCancellationTimestamp,
    quickViewCompositionBarsProps: {
      scan,
      quickViewId: quickViewConfig.id,
      filteredStatistics,
      activeFilterStatistics,
      isFilteredDataTransitioning,
      onTriageSegmentClick,
      onFixSegmentClick,
      onScaSegmentClick,
      triageSelectedSegments,
      fixSelectedSegments,
      scaSelectedSegments,
    },
  } satisfies ScanDetailsHeaderPureProps
}

function useAnalyzeScan({ scanId }: { scanId: string }) {
  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Failed to trigger analysis' })

  const analyzeScanMutation = usePostAnalyzeScan({
    onSuccess: () => {
      addToast({
        message: <>Analysis triggered successfully</>,
        variant: 'success',
      })
    },
    onError: showMutationError,
  })

  const handleAnalyzeScan = useCallback(
    (batchSize?: number) => {
      if (batchSize !== undefined && batchSize > 0) {
        initializeFirstBatch(scanId, batchSize)
      }
      analyzeScanMutation.mutate({ scanId })
    },
    [scanId, analyzeScanMutation]
  )

  const handleReanalyzeScan = useCallback(() => {
    analyzeScanMutation.mutate({ scanId })
  }, [scanId, analyzeScanMutation])

  return { handleAnalyzeScan, handleReanalyzeScan }
}

// #endregion

/**
 * Whether the Resolution panel replaces the composition treatment for this scan.
 *
 * Every SAST scan gets the panel, in every lifecycle state: its empty states cover a scan whose
 * analysis has not started, was cancelled, or is still running its first batch. SCA scans keep the
 * composition treatment, since the panel draws the SAST resolution taxonomy (triage outcomes and
 * fixes) while SCA findings compose from exploitability classifications.
 */
export const shouldShowResolutionPanel = ({ scan }: { scan: Pick<ScanResponse, 'detector'> | undefined }): boolean =>
  !!scan && !isScaTool(scan.detector)

// #region Shared Props

export type ScanDetailsHeaderPureProps = {
  scan?: ScanResponse
  isScanLoading: boolean
  isScanError: boolean
  repository?: Repository
  isRepositoryLoading: boolean
  isRepositoryError: boolean
  scanDetailsHeaderState?: ScanLifecycleState
  isScanDetailsHeaderStateLoading: boolean
  isScanDetailsHeaderStateError: boolean
  onDeleteScan?: () => unknown
  onCancelScan?: () => unknown
  isScanInputsExpired?: boolean
  onAnalyzeScan: () => unknown
  isStale?: boolean
  isLatest?: boolean
  onViewLatest?: () => unknown
  onSkipToInProgressAnalysis?: () => unknown
  hasCancellation?: boolean
  latestCancellationTimestamp?: string
  quickViewCompositionBarsProps: QuickViewCompositionBarsProps
}

// #endregion

// #region Shared Components

export type CompositionBarsContentProps = {
  scanDetailsHeaderState?: ScanLifecycleState
  isScanDetailsHeaderStateLoading: boolean
  isScanDetailsHeaderStateError: boolean
  quickViewCompositionBarsProps: QuickViewCompositionBarsProps
  onSkipToInProgressAnalysis?: () => unknown
  hasCancellation?: boolean
}

export const CompositionBarsContent: React.FC<CompositionBarsContentProps> = ({
  scanDetailsHeaderState,
  isScanDetailsHeaderStateLoading,
  isScanDetailsHeaderStateError,
  quickViewCompositionBarsProps,
  onSkipToInProgressAnalysis,
  hasCancellation = false,
}) =>
  match({
    scanDetailsHeaderState,
    isScanDetailsHeaderStateLoading,
    isScanDetailsHeaderStateError,
    hasCancellation,
  })
    .with({ isScanDetailsHeaderStateLoading: true }, () => (
      <div className={styles.spinnerContainer}>
        <Spinner size="16" label="Loading finding metrics..." />
      </div>
    ))
    .with({ isScanDetailsHeaderStateError: true }, () => (
      <span className={typography.paragraph} style={{ color: mappedColors.neutral.neutral30, margin: 0 }}>
        Error loading finding metrics, please refresh
      </span>
    ))
    .with({ scanDetailsHeaderState: undefined }, () => (
      <span className={typography.paragraph} style={{ color: mappedColors.neutral.neutral30, margin: 0 }}>
        Scan findings have not been found, does the scan exist?
      </span>
    ))
    .with({ scanDetailsHeaderState: { type: 'initial-unanalyzed-scan' }, hasCancellation: true }, () => (
      <CompositionBarWithLabel type="cancelled" />
    ))
    .with({ scanDetailsHeaderState: { type: 'initial-unanalyzed-scan' } }, () => (
      <CompositionBarWithLabel type="not-initialized" />
    ))
    .with(
      {
        scanDetailsHeaderState: { type: 'first-analysis-batch-in-progress' },
        hasCancellation: true,
      },
      ({ scanDetailsHeaderState: { statistics, analyzedCount } }) => {
        // If a cancellation exists and no findings were analyzed end-to-end before it, show the
        // cancelled empty-state rather than the in-progress bar. The lifecycle state is sticky here
        // because `isFirstBatchComplete` counts only analyzed findings, not cancelled ones — so a scan
        // whose entire first batch was cancelled stays classified as "first batch in progress"
        // even though nothing is actually running.
        if (analyzedCount === 0) {
          return <CompositionBarWithLabel type="cancelled" />
        }
        return (
          <div onDoubleClick={onSkipToInProgressAnalysis}>
            <CompositionBarWithLabel
              type="in-progress"
              numerator={Math.min(analyzedCount, statistics.total)}
              denominator={statistics.total}
            />
          </div>
        )
      }
    )
    .with(
      { scanDetailsHeaderState: { type: 'first-analysis-batch-in-progress' } },
      ({ scanDetailsHeaderState: { firstBatchTotal, analyzedCount } }) => (
        <div onDoubleClick={onSkipToInProgressAnalysis}>
          <CompositionBarWithLabel
            type="in-progress"
            numerator={Math.min(analyzedCount, firstBatchTotal)}
            denominator={firstBatchTotal}
          />
        </div>
      )
    )
    .with(
      {
        scanDetailsHeaderState: {
          type: P.union('findings-with-analysis-complete', 'findings-with-analysis-complete-and-in-progress'),
        },
      },
      () => <QuickViewCompositionBars {...quickViewCompositionBarsProps} />
    )
    .exhaustive()

export type AnalysisUpdatingCountProps = {
  inProgressCount: number
}

export const AnalysisUpdatingCount: React.FC<AnalysisUpdatingCountProps> = ({ inProgressCount }) => (
  <span className={styles.updatingTextSmall}>{inProgressCount} updating</span>
)

export const AnalysisUpdatingStatus: React.FC<AnalysisUpdatingCountProps> = ({ inProgressCount }) => (
  <div className={styles.updatingContainer}>
    <Spinner size="24" variant="updating" label="Updating findings" />
    <AnalysisUpdatingCount inProgressCount={inProgressCount} />
  </div>
)

// #endregion

// #region Shared Metrics

const HIDDEN_METRICS_QUICK_VIEWS: ReadonlySet<FindingQuickViewId> = new Set([
  'unanalyzed',
  'sca-unanalyzed',
  'sca-exploitable',
  'sca-not-exploitable',
])

const getFixMetric = (statistics: FindingAggregateResponseStatistics) => ({
  count: statistics.fix.outcome.has_fix,
  total: getFixableFindingsCount(statistics.fix),
})

export type QuickViewAnalysisMetricsProps = {
  tool: Tool
  quickViewId: FindingQuickViewId
  filteredStatistics?: FindingAggregateResponseStatistics
  globalStats: FindingAggregateResponseStatistics
  isFilteredDataTransitioning?: boolean
  hasActiveFilters?: boolean
}

export const QuickViewAnalysisMetrics: React.FC<QuickViewAnalysisMetricsProps> = ({
  tool,
  quickViewId,
  filteredStatistics,
  globalStats,
  isFilteredDataTransitioning,
  hasActiveFilters,
}) => {
  if (isFilteredDataTransitioning || HIDDEN_METRICS_QUICK_VIEWS.has(quickViewId)) return null

  const stats = filteredStatistics ?? globalStats
  if (stats.total === 0) return null

  const scaTotal = isScaTool(tool) ? getScaProcessedTotal(stats) : stats.total
  const triageTotal =
    stats.triage.status.completed + stats.triage.status.failed + stats.triage.status.no_recommendations

  const hideFixMetric = quickViewId === 'dismissable' || (hasActiveFilters && stats.fix.outcome.has_fix === 0)

  return (
    <AnalysisMetrics
      tool={tool}
      triage={{ count: stats.triage.status.completed, total: triageTotal }}
      fix={hideFixMetric ? undefined : getFixMetric(stats)}
      sca={{ count: stats.sca.status.completed, total: scaTotal }}
    />
  )
}

// #endregion
